import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthResponse,
  ChangePasswordRequest,
  LoginRequest,
  RefreshTokenRequest,
  RegisterRequest,
  UpdateProfileRequest,
  UserProfile,
} from '../models/auth.model';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly baseUrl = `${environment.apiBaseUrl}/auth`;
  private readonly usersUrl = `${environment.apiBaseUrl}/users/me`;

  register(request: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/register`, request)
      .pipe(tap((response) => this.persistAuthResponse(response)));
  }

  login(request: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/login`, request)
      .pipe(tap((response) => this.persistAuthResponse(response)));
  }

  refresh(): Observable<AuthResponse> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    const request: RefreshTokenRequest = { refreshToken: refreshToken ?? '' };
    return this.http
      .post<AuthResponse>(`${this.baseUrl}/refresh`, request)
      .pipe(tap((response) => this.persistAuthResponse(response)));
  }

  logout(): Observable<void> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    const request: RefreshTokenRequest = { refreshToken: refreshToken ?? '' };
    return this.http.post<void>(`${this.baseUrl}/logout`, request).pipe(
      tap(() => this.tokenStorage.clear()),
    );
  }

  /** خروج فوری بدون منتظر ماندن برای پاسخ سرور (مثلاً وقتی Refresh Token هم منقضی شده) */
  forceLocalLogout(): void {
    this.tokenStorage.clear();
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.usersUrl);
  }

  updateProfile(request: UpdateProfileRequest): Observable<UserProfile> {
    return this.http
      .put<UserProfile>(this.usersUrl, request)
      .pipe(tap((profile) => this.tokenStorage.setUser(profile)));
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.usersUrl}/change-password`, request);
  }

  deleteAccount(): Observable<void> {
    return this.http.delete<void>(this.usersUrl).pipe(tap(() => this.tokenStorage.clear()));
  }

  private persistAuthResponse(response: AuthResponse): void {
    this.tokenStorage.setTokens(response.accessToken, response.refreshToken);
    this.tokenStorage.setUser(response.user);
  }
}
